// Document tabs (TODO §14.7.1, §14.7.6).
//
// Tabs are **renderer-side**. Each one is a deck at its own `/d/:deckId` route (§12.1) inside
// the single BrowserWindow, so switching tabs is the route change §12.2 already handles —
// remount the editor, keep one store, one write session. The alternative, one window per
// deck, would have put the web and the desktop on different code paths for the same feature.
//
// The shape of the state (which decks, in which order, which is in front) belongs to the
// WINDOW, not to any deck, so it is persisted in `userData` through the shell bridge
// (§14.7.6) and not in the deck database. It is also the only thing persisted: the decks stay
// in IndexedDB (§14.7.5). Losing this file costs the user their tab strip and nothing else.
//
// A module-level store rather than context, for the same reason `deckSession.ts` is one: the
// native menu (§14.7.4) has to drive it from outside React's tree.

import { useSyncExternalStore } from "react";

import { subscribeDeckMessages } from "@/lib/deckChannel";
import { desktopShell } from "@/lib/desktopBridge";
import { listDecks } from "@/lib/idb";

export interface DesktopTabsState {
  /** Deck ids in tab order. */
  deckIds: string[];
  /** The deck showing now, or null on the start screen. */
  activeDeckId: string | null;
  /** Titles for the strip, by deck id — read from the §12.3 index, never from `decks`. */
  titles: Record<string, string>;
  /** False until the persisted state has been read back; the strip holds off until then. */
  ready: boolean;
}

const EMPTY: DesktopTabsState = {
  deckIds: [],
  activeDeckId: null,
  titles: {},
  ready: false,
};

let state: DesktopTabsState = EMPTY;
const listeners = new Set<(s: DesktopTabsState) => void>();

function setState(next: Partial<DesktopTabsState>): void {
  const merged = { ...state, ...next };
  // `useSyncExternalStore` compares by identity, so bail out when nothing actually moved —
  // otherwise every deck-updated broadcast re-renders the whole strip.
  if (
    merged.activeDeckId === state.activeDeckId &&
    merged.ready === state.ready &&
    merged.deckIds === state.deckIds &&
    merged.titles === state.titles
  ) {
    return;
  }
  state = merged;
  for (const fn of [...listeners]) fn(state);
}

export function getDesktopTabs(): DesktopTabsState {
  return state;
}

export function subscribeDesktopTabs(
  fn: (s: DesktopTabsState) => void,
): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** The tab state, live. A hook over the module store — see the note at the top for why. */
export function useDesktopTabs(): DesktopTabsState {
  return useSyncExternalStore(
    subscribeDesktopTabs,
    getDesktopTabs,
    getDesktopTabs,
  );
}

function persist(): void {
  desktopShell()?.setOpenTabs({
    deckIds: state.deckIds,
    activeDeckId: state.activeDeckId,
  });
}

async function readTitles(deckIds: string[]): Promise<Record<string, string>> {
  const index = await listDecks();
  const titles: Record<string, string> = {};
  for (const entry of index) {
    if (deckIds.includes(entry.deckId)) titles[entry.deckId] = entry.title;
  }
  return titles;
}

let loaded = false;

/**
 * §14.7.6 Restore the previously open tabs. Ids that no longer resolve to a deck are dropped:
 * a deck deleted from the documents home (or in another window) must not come back as a tab
 * that opens a "not found".
 *
 * Resolves with the deck the window should show — the caller navigates, because routing is
 * React Router's job and this module has no business touching history.
 */
export async function restoreDesktopTabs(): Promise<string | null> {
  const shell = desktopShell();
  if (!shell || loaded) return state.activeDeckId;
  loaded = true;
  try {
    const [persisted, index] = await Promise.all([
      shell.getOpenTabs(),
      listDecks(),
    ]);
    const alive = new Set(index.map((entry) => entry.deckId));
    const deckIds = persisted.deckIds.filter((id) => alive.has(id));
    const activeDeckId =
      persisted.activeDeckId && deckIds.includes(persisted.activeDeckId)
        ? persisted.activeDeckId
        : (deckIds[0] ?? null);
    const titles: Record<string, string> = {};
    for (const entry of index)
      if (deckIds.includes(entry.deckId)) titles[entry.deckId] = entry.title;
    setState({ deckIds, activeDeckId, titles, ready: true });
    if (deckIds.length !== persisted.deckIds.length) persist();
    return activeDeckId;
  } catch (err) {
    // A tab strip is a convenience; failing to restore it must not keep the studio shut.
    console.warn("[desktop-tabs] could not restore open tabs", err);
    setState({ ready: true });
    return null;
  }
}

/** Open (or focus) a deck's tab. Called by the route itself, so any way in registers a tab. */
export function openDesktopTab(deckId: string): void {
  if (!desktopShell() || !deckId) return;
  const deckIds = state.deckIds.includes(deckId)
    ? state.deckIds
    : [...state.deckIds, deckId];
  if (deckIds === state.deckIds && state.activeDeckId === deckId) return;
  setState({ deckIds, activeDeckId: deckId });
  persist();
  if (!state.titles[deckId]) void refreshTitles();
}

/**
 * Close a tab. Returns the deck the window should show next, or null for the start screen —
 * again, the caller navigates.
 */
export function closeDesktopTab(deckId: string): string | null {
  if (!desktopShell()) return state.activeDeckId;
  const at = state.deckIds.indexOf(deckId);
  if (at === -1) return state.activeDeckId;
  const deckIds = state.deckIds.filter((id) => id !== deckId);
  // The neighbour to the right, else the one to the left — what every tabbed app does, and
  // what stops a close from throwing the user back to the start screen with tabs still open.
  const activeDeckId =
    state.activeDeckId === deckId
      ? (deckIds[at] ?? deckIds[at - 1] ?? null)
      : state.activeDeckId;
  setState({ deckIds, activeDeckId });
  persist();
  return activeDeckId;
}

/** ⌃Tab / ⌃⇧Tab (§14.7.4). Wraps around; null when there is nothing to switch to. */
export function cycleDesktopTab(delta: number): string | null {
  const { deckIds, activeDeckId } = state;
  if (deckIds.length === 0) return null;
  const at = activeDeckId ? deckIds.indexOf(activeDeckId) : -1;
  const next =
    deckIds[
      (((at + delta) % deckIds.length) + deckIds.length) % deckIds.length
    ];
  return next ?? null;
}

/** Re-read titles from the §12.3 index — a rename anywhere must show up in the strip. */
export async function refreshTitles(): Promise<void> {
  if (!desktopShell() || state.deckIds.length === 0) return;
  const titles = await readTitles(state.deckIds);
  setState({ titles });
}

// §12.5.3 Any deck change anywhere — this window, another window, the documents home — can
// rename or delete something the strip is showing. One debounced re-read covers all of it.
if (typeof window !== "undefined" && desktopShell()) {
  let timer = 0;
  subscribeDeckMessages((msg) => {
    if (msg.type === "deck-deleted" && state.deckIds.includes(msg.deckId)) {
      closeDesktopTab(msg.deckId);
      return;
    }
    window.clearTimeout(timer);
    timer = window.setTimeout(() => void refreshTitles(), 300);
  });
}
